import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { TimeMode } from '@types';
import { SiteMeta } from '@src/types/SiteMeta';
import { currentTzString, getLocale } from '@utils';

dayjs.extend(utc);
dayjs.extend(timezone);

export function useChartTimezoneConfig(timeMode: TimeMode, siteMeta: SiteMeta) {
  const currentTimeZoneString = currentTzString({
    timeMode,
    lat: siteMeta.GPSLat,
    long: siteMeta.GPSLong,
  });

  const tzOffset = getLocale({
    timeMode,
    lat: siteMeta.GPSLat,
    long: siteMeta.GPSLong,
    returnOffset: true,
  }) as number;

  const tzString = getLocale({
    timeMode,
    lat: siteMeta.GPSLat,
    long: siteMeta.GPSLong,
    returnOffset: false,
  }) as string;

  return {
    currentTimeZoneString,
    tzOffset,
    tzString,
  };
}

export function getHighchartsTimeConfig(tzOffset: number) {
  return {
    useUTC: true,
    timezoneOffset: tzOffset,
  };
}

export function formatChartDate(
  timestamp: number | string,
  format: string,
  timeMode: TimeMode,
  siteMeta: SiteMeta,
): string {
  const tzString = getLocale({
    timeMode,
    lat: siteMeta.GPSLat,
    long: siteMeta.GPSLong,
    returnOffset: false,
  }) as string;

  return dayjs(timestamp).tz(tzString).format(format);
}

export function formatTimestampForDisplay(
  timestamp: string,
  timeMode: TimeMode | undefined,
  siteMeta: SiteMeta | undefined,
): string {
  if (!timeMode || !siteMeta) {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  return formatChartDate(timestamp, 'MMM D, HH:mm:ss', timeMode, siteMeta);
}

export function getChartXAxisFormatter(timeMode: TimeMode, siteMeta: SiteMeta) {
  return function (this: { value: number }) {
    return formatChartDate(this.value, 'MMM DD HH:mm', timeMode, siteMeta);
  };
}
